/**
 * RequirementsTable — DataTable + TableToolbar wrapper for the
 * Requirements half of Step 8. FilterDrawer keyed on `cal_level`
 * multi-select (4 ISO 21434 levels).
 */
import React, { useEffect, useMemo, useState } from 'react';
import {
  DataTable,
  TableToolbar,
  TablePagination,
  RowActionsMenu,
} from '../../../../components/ui/DataTable';
import FilterDrawer from '../../../../components/ui/DataTable/FilterDrawer';
import { Icon } from '../../../../components/ui/Icon';
import { Button } from '../../../../components/ui/Button2';
import {
  REQUIREMENT_COLUMNS_READ,
  REQUIREMENT_COLUMNS_EDIT,
  REQUIREMENT_ACTIONS_COLUMN_WIDTH,
  CAL_LEVELS,
  REQUIREMENT_TYPES,
  type RequirementRecord,
} from './requirementsFieldSchema';
import styles from './Step8Tables.module.css';

const DataTableAny = DataTable as unknown as React.ComponentType<any>;
const TableToolbarAny = TableToolbar as unknown as React.ComponentType<any>;
const TablePaginationAny = TablePagination as unknown as React.ComponentType<any>;
const RowActionsMenuAny = RowActionsMenu as unknown as React.ComponentType<any>;
const FilterDrawerAny = FilterDrawer as unknown as React.ComponentType<any>;
// Button2 is a JSX forwardRef primitive; cast for use from TSX (mirrors the
// ButtonAny convention across the step-components).
const ButtonAny = Button as unknown as React.ComponentType<any>;

const DEFAULT_PAGE_SIZE = 20;

interface SortState { column: string; direction: 'asc' | 'desc'; }

export interface RequirementsTableProps {
  requirements: RequirementRecord[];
  loading?: boolean;
  emptyMessage?: string;
  editable?: boolean;
  onAdd?: () => void;
  onEdit?: (req: RequirementRecord) => void;
  onDelete?: (req: RequirementRecord) => void;
  onExport?: () => void;
  onImport?: (file: File) => void;
}

function sortRows(rows: RequirementRecord[], sort: SortState | null): RequirementRecord[] {
  if (!sort) return rows;
  const dir = sort.direction === 'asc' ? 1 : -1;
  return [...rows].sort((a, b) => {
    const av = (a as Record<string, unknown>)[sort.column];
    const bv = (b as Record<string, unknown>)[sort.column];
    if (av == null && bv == null) return 0;
    if (av == null) return 1;
    if (bv == null) return -1;
    return String(av).localeCompare(String(bv)) * dir;
  });
}

function searchRows(rows: RequirementRecord[], query: string): RequirementRecord[] {
  const q = query.trim().toLowerCase();
  if (!q) return rows;
  return rows.filter((r) =>
    Object.values(r).some((v) =>
      v == null
        ? false
        : Array.isArray(v)
        ? v.join(' ').toLowerCase().includes(q)
        : String(v).toLowerCase().includes(q),
    ),
  );
}

export default function RequirementsTable({
  requirements,
  loading,
  emptyMessage = 'No cybersecurity requirements yet.',
  editable = false,
  onAdd,
  onEdit,
  onDelete,
  onExport,
  onImport,
}: RequirementsTableProps) {
  const fileInputRef = React.useRef<HTMLInputElement | null>(null);
  const [filterOpen, setFilterOpen] = useState(false);
  const [calValues, setCalValues] = useState<string[]>([]);
  const [typeValues, setTypeValues] = useState<string[]>([]);
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState<SortState | null>(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState<number>(DEFAULT_PAGE_SIZE);

  const calCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const c of CAL_LEVELS) counts[c.value] = 0;
    for (const r of requirements) {
      const k = String(r.cal_level ?? '');
      if (k in counts) counts[k] += 1;
    }
    return counts;
  }, [requirements]);

  const typeCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const t of REQUIREMENT_TYPES) counts[t.value] = 0;
    for (const r of requirements) {
      const k = String(r.requirement_type ?? '');
      if (k in counts) counts[k] += 1;
    }
    return counts;
  }, [requirements]);

  const calFiltered = useMemo(
    () => (calValues.length === 0
      ? requirements
      : requirements.filter((r) => calValues.includes(String(r.cal_level ?? '')))),
    [requirements, calValues],
  );
  const typeFiltered = useMemo(
    () => (typeValues.length === 0
      ? calFiltered
      : calFiltered.filter((r) => typeValues.includes(String(r.requirement_type ?? '')))),
    [calFiltered, typeValues],
  );
  const searched = useMemo(() => searchRows(typeFiltered, search), [typeFiltered, search]);
  const sorted = useMemo(() => sortRows(searched, sort), [searched, sort]);

  const totalPages = Math.max(1, Math.ceil(sorted.length / pageSize));
  useEffect(() => { if (page > totalPages) setPage(1); }, [page, totalPages]);

  const paged = useMemo(
    () => sorted.slice((page - 1) * pageSize, page * pageSize),
    [sorted, page, pageSize],
  );

  const keyed = useMemo(
    () => paged.map((r, i) => ({ ...r, _rowKey: `${r.req_id ?? 'row'}::${(page - 1) * pageSize + i}` })),
    [paged, page, pageSize],
  );

  const columns = useMemo(() => {
    if (!editable) return REQUIREMENT_COLUMNS_READ;
    return [
      ...REQUIREMENT_COLUMNS_EDIT,
      {
        key: '_actions',
        label: '',
        align: 'right',
        width: REQUIREMENT_ACTIONS_COLUMN_WIDTH,
        sortable: false,
        render: (row: RequirementRecord) => (
          <RowActionsMenuAny
            ariaLabel={`Actions for ${row.req_id}`}
            actions={[
              { id: 'edit', label: 'Edit', icon: <Icon name="edit" size={14} />, onClick: () => onEdit?.(row) },
              { id: 'delete', label: 'Delete', icon: <Icon name="delete" size={14} />, danger: true, onClick: () => onDelete?.(row) },
            ]}
          />
        ),
      },
    ];
  }, [editable, onEdit, onDelete]);

  const calFilter = {
    key: 'cal_level',
    label: 'CAL',
    values: calValues,
    onChange: (next: string[]) => { setCalValues(next); setPage(1); },
    options: CAL_LEVELS.map((c) => ({ value: c.value, label: c.label, count: calCounts[c.value] ?? 0 })),
  };

  const typeFilter = {
    key: 'requirement_type',
    label: 'Type',
    values: typeValues,
    onChange: (next: string[]) => { setTypeValues(next); setPage(1); },
    options: REQUIREMENT_TYPES.map((t) => ({ value: t.value, label: t.label, count: typeCounts[t.value] ?? 0 })),
  };

  const toolbarActions = (
    <div className={styles.toolbarActions}>
      {onExport && sorted.length > 0 && (
        <ButtonAny variant="outline" size="sm" onClick={onExport}>
          <Icon name="upload" size={14} /> Export CSV
        </ButtonAny>
      )}
      {editable && onImport && (
        <>
          <ButtonAny variant="outline" size="sm" onClick={() => fileInputRef.current?.click()}>
            <Icon name="download" size={14} /> Import
          </ButtonAny>
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv,.json"
            style={{ display: 'none' }}
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) onImport(f);
              if (e.target) e.target.value = '';
            }}
          />
        </>
      )}
      {editable && onAdd && (
        <ButtonAny variant="default" size="sm" onClick={onAdd}>
          <Icon name="add" size={14} /> Add Requirement
        </ButtonAny>
      )}
    </div>
  );

  return (
    <div className={styles.tableShell}>
      <FilterDrawerAny isOpen={filterOpen} onClose={() => setFilterOpen(false)} filters={[calFilter, typeFilter]} />
      <DataTableAny
        columns={columns}
        data={keyed}
        rowKey="_rowKey"
        sort={sort}
        onSortChange={(column: string | null, direction: 'asc' | 'desc' | null) =>
          setSort(column && direction ? { column, direction } : null)
        }
        loading={loading}
        emptyMessage={emptyMessage}
        toolbar={
          <TableToolbarAny
            search={{
              value: search,
              onChange: (v: string) => { setSearch(v); setPage(1); },
              placeholder: 'Search requirements…',
            }}
            count={{ value: sorted.length, label: 'Requirements' }}
            onFilterToggle={() => setFilterOpen((o) => !o)}
            filterButtonActive={filterOpen || calValues.length > 0 || typeValues.length > 0}
            actions={toolbarActions}
          />
        }
        pagination={
          <TablePaginationAny
            currentPage={page}
            totalItems={sorted.length}
            itemsPerPage={pageSize}
            onPageChange={setPage}
            onItemsPerPageChange={(size: number) => { setPageSize(size); setPage(1); }}
          />
        }
      />
    </div>
  );
}
